import { animated, useSpring } from "@react-spring/web";
import useWindowSize from "utils/customHooks/useWindowSize";
import styled from "styled-components/macro";
import { useAtom } from "jotai";
import { clickedTileRectAtom } from "components/Dashboard/Tiles/ProcessTile/ProcessTile";
import {
  useAnimationsEnabled,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { PNL_COLORS_CSS } from "components/Dashboard/HubletView/HubletViewStyles/PNL_COLORS_CSS";
import { useEffect, useLayoutEffect, useState } from "react";
import { usePrevious } from "react-use";
import { useBreakpoints } from "constants/constants";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useSelector } from "store/useSelector";

const HUBLET_VIEW_SELECTOR = "[class*=HubletViewStyles-sc]";

export default function AnimationFromClickedTileToHubletViewWrapper() {
  const animationsEnabled = useAnimationsEnabled();
  const { isHubletPage, isProcessTilesPage } = useGetPageFromPath();

  // all-workschedules-page can be too demanding for this animation
  const { isAllWorkSchedulesPage } = useGetPageFromPath();

  return animationsEnabled &&
    (isProcessTilesPage || isHubletPage) &&
    !isAllWorkSchedulesPage ? (
    <AnimationFromClickedTileToHubletView />
  ) : null;
}
function AnimationFromClickedTileToHubletView() {
  const [clickedTileRect, setClickedTileRect] = useAtom(clickedTileRectAtom);
  const { isHubletPage, isProcessTilesPage } = useGetPageFromPath();
  const { isDesktopXlOrLarger, isMobileOrLarger } = useBreakpoints();
  const wasHubletPage = usePrevious(isHubletPage);
  const wasProcessTilesPage = usePrevious(isProcessTilesPage);
  // when going from process tiles page -> hublet page: this component animates from the clicked tile to the hublet view
  // when going from hublet page -> process tiles page: this component animates from the hublet view to the clicked tile
  const { innerHeight, innerWidth } = useWindowSize();

  const [hidden, setHidden] = useState(true);
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const hubletPageProcessId = useProcessIdOnSingleProcessPage();
  const lastDataDumpRow = hubletPageProcessId
    ? dataDumpsFullRangeByProcess[hubletPageProcessId]?.lastSegment
    : null;

  // sometimes landing on processTilesPage we don't have the correct clickedTileRect
  // e.g. we can go timelinePage -> hubletPage -> back to processTilesPage
  // e.g. we can visit the hubletPage url directly -> back to processTilesPage
  // so we need to ensure that we've stored the correct processId, or we'll animate back to the wrong tile
  useLayoutEffect(() => {
    if (
      hubletPageProcessId &&
      (!clickedTileRect?.processId ||
        hubletPageProcessId !== clickedTileRect.processId)
    ) {
      setClickedTileRect(prev => ({
        ...prev,
        processId: hubletPageProcessId ?? prev?.processId,
        pnlCategory: lastDataDumpRow?.pnlCategory ?? prev?.pnlCategory,
      }));
    }
  }, [
    clickedTileRect?.processId,
    setClickedTileRect,
    hubletPageProcessId,
    lastDataDumpRow?.pnlCategory,
  ]);

  // when going back to process tiles page, the tile might be in a different position since the last click e.g. if the tiles are sorted & the sort order changed, or if page scrolled vertically since last click
  // so, grab the tile's rect again
  useEffect(() => {
    if (isProcessTilesPage && clickedTileRect?.processId) {
      // need to setTimeout to allow large-tiles mode to calculate correct size (startTransition causes a small resize glitch)
      setTimeout(() => {
        const processTileElement = document.querySelector<HTMLElement>(
          `.process-tile-${clickedTileRect.processId}`
        );
        const tileRect = processTileElement?.getBoundingClientRect();
        setClickedTileRect(prev => ({
          ...prev,
          tileRect: tileRect ?? prev?.tileRect,
          pnlCategory: lastDataDumpRow?.pnlCategory ?? prev?.pnlCategory,
        }));
      });
    }
    // don't need to update every time pnlCategory changes, only when landing on processTilesPage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessTilesPage, setClickedTileRect, clickedTileRect?.processId]);

  // hide and unhide animatingtile
  useLayoutEffect(() => {
    if (!clickedTileRect?.tileRect) {
      return;
    }
    if (
      (isProcessTilesPage && wasHubletPage) ||
      (isHubletPage && wasProcessTilesPage)
    ) {
      setHidden(false);
      if (isHubletPage) {
        hideHubletView();
      } else if (isProcessTilesPage && clickedTileRect.processId) {
        hideProcessTile(clickedTileRect.processId);
      }
    }
  }, [
    isProcessTilesPage,
    wasHubletPage,
    isHubletPage,
    wasProcessTilesPage,
    clickedTileRect?.processId,
    clickedTileRect?.tileRect,
  ]);

  const [immediate, setImmediate] = useState(false);
  const prevClickedTileRect = usePrevious(clickedTileRect);

  const clickedTileRectChanged = prevClickedTileRect !== clickedTileRect;

  // problem: when clicking back and forth hublet<->processTiles, the animating-tile moves between the tiles, which ruins the magic
  // solution: if clicked process changed, deactivate animation, so we can move the animating-tile to the new clicked tile immediately
  useLayoutEffect(() => {
    if (
      prevClickedTileRect?.processId &&
      clickedTileRect?.processId &&
      prevClickedTileRect.processId !== clickedTileRect.processId
    ) {
      setImmediate(true);
      setTimeout(() => setImmediate(false), 0);
      // also looks less awkward if we immediately unhide the hidden tile
      unhideProcessTile(prevClickedTileRect.processId);
    }
  }, [prevClickedTileRect, clickedTileRect]);

  const navbarOffsetTop =
    (isMobileOrLarger ? 64 : 56) * (isDesktopXlOrLarger ? 1.5 : 1);
  const hubletViewRect = document
    .querySelector(HUBLET_VIEW_SELECTOR)
    ?.getBoundingClientRect();
  const springPosition = useSpring({
    transformOrigin: "center center",
    ...(!clickedTileRect?.tileRect
      ? {}
      : isHubletPage && hubletViewRect
        ? // hublet view size & position
          {
            width: hubletViewRect.width,
            height: hubletViewRect.height,
            top: hubletViewRect.top - navbarOffsetTop,
            left: hubletViewRect.left,
          }
        : // process tile size & position
          {
            width: clickedTileRect.tileRect.width,
            height: clickedTileRect.tileRect.height,
            top: clickedTileRect.tileRect.top - navbarOffsetTop,
            left: clickedTileRect.tileRect.left,
          }),
    config: {
      tension: 170,
      friction: 27,
      mass: 1,
    },
    immediate:
      !clickedTileRect?.tileRect ||
      (clickedTileRectChanged && isHubletPage) ||
      immediate, // immediately move to the clickedTileRect position when it changes
    onRest: () => {
      // after visible animation ...
      if (!hidden) {
        setHidden(true); // hide the animating tile

        // unhide hidden element before animatingtile's opacity starts animating to 0
        if (isHubletPage) {
          unhideHubletView();
        } else if (isProcessTilesPage && clickedTileRect?.processId) {
          unhideProcessTile(clickedTileRect.processId);
        }
      }
    },
  });

  const springOpacity = useSpring({
    opacity: hidden ? 0 : 1,
    immediate: !hidden, // unhide immediately
    config: {
      tension: 170,
      friction: 27,
      mass: 0.25,
      clamp: true,
    },
  });
  return (
    <AnimationStyles
      className="doNotPrint"
      $height={innerHeight}
      $width={innerWidth}
      $tileRect={clickedTileRect}
    >
      <animated.div
        style={{ ...springPosition, ...springOpacity }}
        className={`animatingtile background-${clickedTileRect?.pnlCategory}`}
      />
    </AnimationStyles>
  );
}
const AnimationStyles = styled.div<{
  $height: number;
  $width: number;
  $tileRect: DOMRect | null;
}>`
  pointer-events: none;
  position: fixed;
  width: 100%;
  height: 100vh;
  z-index: 1;
  ${PNL_COLORS_CSS}
  /* entering from hublet page */
  .animatingtile {
    position: absolute;
  }
`;

function setHubletViewVisibility(visibleOrHidden: "visible" | "hidden") {
  const hubletViewElement =
    document.querySelector<HTMLElement>(HUBLET_VIEW_SELECTOR);
  if (hubletViewElement) {
    hubletViewElement.style.visibility = visibleOrHidden;
  }
  const hubletViewBGElement = document.querySelector<HTMLElement>(
    `${HUBLET_VIEW_SELECTOR} .background`
  );
  if (hubletViewBGElement) {
    hubletViewBGElement.style.visibility = visibleOrHidden;
  }
}

function hideHubletView() {
  setHubletViewVisibility("hidden");
}
function unhideHubletView() {
  setHubletViewVisibility("visible");
}

function setProcessTileVisibility(
  processId: string,
  visibleOrHidden: "visible" | "hidden"
) {
  const processTileElement = document.querySelector<HTMLElement>(
    `.process-tile-${processId}`
  );
  if (processTileElement) {
    processTileElement.style.visibility = visibleOrHidden;
  }
}

function hideProcessTile(processId: string) {
  setProcessTileVisibility(processId, "hidden");
}
function unhideProcessTile(processId: string) {
  setProcessTileVisibility(processId, "visible");
}
